import { createContext, useContext, useState } from 'react';

// The viewer's VIEWPOINT: lens weights + chosen trust engine. There is no
// single trust algorithm here (DECISIONS.md 2026-06-11): the combined score is
// one perspective blend, and the blend belongs to the viewer, not the system.
// Weights recombine the per-lens scores CLIENT-SIDE — the backend keeps
// serving the plain unweighted mean, which equals weights-all-1 here, so the
// default view matches the API. Since wave 5 these controls live in ONE
// global drawer (ViewpointDrawer, reachable from the nav on every page);
// views no longer own private copies.

const STORAGE_KEY = 'worldview-lens-weights';
const AXIS_KEY = 'worldview-axis-weights';
const ENGINE_KEY = 'worldview-engine';
export const DEFAULT_ENGINE = 'eigentrust';

const LensWeightsContext = createContext({
  weights: {},
  setWeight: () => {},
  reset: () => {},
  axisWeights: {},
  setAxisWeight: () => {},
  engine: DEFAULT_ENGINE,
  setEngine: () => {},
});

export function LensWeightsProvider({ children }) {
  const [weights, setWeights] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  });
  // Standalone-axis weights (vector-trust phase 2). Unlike lens weights,
  // the DEFAULT IS ZERO — the ruling's load-bearing default: an axis exists
  // and is visible the moment its relation name does, but moves nobody's
  // blend until the viewer deliberately raises it here.
  const [axisWeights, setAxisWeights] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(AXIS_KEY)) || {};
    } catch {
      return {};
    }
  });
  const [engine, setEngineState] = useState(() => {
    try {
      return localStorage.getItem(ENGINE_KEY) || DEFAULT_ENGINE;
    } catch {
      return DEFAULT_ENGINE;
    }
  });

  const persist = (next) => {
    try {
      if (Object.keys(next).length === 0) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // private mode — weights still apply for the session
    }
  };

  const setWeight = (id, w) =>
    setWeights((prev) => {
      const next = { ...prev, [id]: w };
      persist(next);
      return next;
    });

  const reset = () =>
    setWeights(() => {
      persist({});
      return {};
    });

  const setAxisWeight = (axis, w) =>
    setAxisWeights((prev) => {
      // weight 0 is the default — drop the key so untouched stays untouched
      const next = { ...prev };
      if (w === 0) delete next[axis];
      else next[axis] = w;
      try {
        if (Object.keys(next).length === 0) localStorage.removeItem(AXIS_KEY);
        else localStorage.setItem(AXIS_KEY, JSON.stringify(next));
      } catch {
        // private mode — weights still apply for the session
      }
      return next;
    });

  const setEngine = (id) => {
    setEngineState(id);
    try {
      if (id === DEFAULT_ENGINE) localStorage.removeItem(ENGINE_KEY);
      else localStorage.setItem(ENGINE_KEY, id);
    } catch {
      // private mode
    }
  };

  return (
    <LensWeightsContext.Provider
      value={{ weights, setWeight, reset, axisWeights, setAxisWeight, engine, setEngine }}
    >
      {children}
    </LensWeightsContext.Provider>
  );
}

export const useLensWeights = () => useContext(LensWeightsContext);

export const lensWeight = (weights, id) => (weights[id] === undefined ? 1 : weights[id]);

// Weighted mean over whatever lens ids are present in `scores`.
// All weights 1 (the default) reproduces the backend's combined score exactly.
export function recombine(scores, weights) {
  let sum = 0;
  let weightSum = 0;
  for (const [id, s] of Object.entries(scores)) {
    const w = lensWeight(weights, id);
    sum += w * s;
    weightSum += w;
  }
  return weightSum > 0 ? sum / weightSum : 0;
}

export const axisWeight = (axisWeights, axis) =>
  axisWeights[axis] === undefined ? 0 : axisWeights[axis];

// The blend with standalone axes joined in (vector-trust phase 2): axes the
// viewer has weighted above ZERO enter the same weighted-mean pool as the
// lenses. All axis weights at the default 0 → identical to recombine(), so
// no band moves until a viewer deliberately opts an axis in. Used where the
// payload serves axes (the object page); other surfaces keep recombine().
export function recombineWithAxes(scores, weights, axes, axisWeights) {
  let sum = 0;
  let weightSum = 0;
  for (const [id, s] of Object.entries(scores)) {
    const w = lensWeight(weights, id);
    sum += w * s;
    weightSum += w;
  }
  for (const a of axes || []) {
    const w = axisWeight(axisWeights, a.axis);
    if (w > 0) {
      sum += w * a.score;
      weightSum += w;
    }
  }
  return weightSum > 0 ? sum / weightSum : 0;
}

// Axis sliders — the drawer's collapsed group. Default 0; raising a slider is
// the ONE act that ever mixes an axis into the viewer's band.
export function AxisWeightSliders({ axes }) {
  const { axisWeights, setAxisWeight } = useLensWeights();
  const touched = axes.filter((a) => axisWeight(axisWeights, a.axis) !== 0);

  return (
    <details open={touched.length > 0}>
      <summary className="trust-muted" style={{ fontSize: '12px', cursor: 'pointer' }}>
        {touched.length > 0
          ? `${touched.length} axis${touched.length === 1 ? '' : 'es'} in your blend · ${axes.length} available`
          : `axes you haven't weighted (${axes.length})`}
      </summary>
      <div className="lens-weights-grid" style={{ gridTemplateColumns: '1fr', marginTop: '6px' }}>
        {axes.map((a) => {
          const w = axisWeight(axisWeights, a.axis);
          return (
            <label key={a.axis} title={`${a.count} activated edge(s) on this axis`}>
              <span className="lens-weights-name">
                {a.axis}
                <strong>{w.toFixed(1)}×</strong>
              </span>
              <input
                type="range"
                min={0}
                max={2}
                step={0.1}
                value={w}
                onChange={(e) => setAxisWeight(a.axis, parseFloat(e.target.value))}
              />
            </label>
          );
        })}
      </div>
    </details>
  );
}

// The slider grid itself — rendered by the global ViewpointDrawer.
export function LensWeightSliders({ lenses }) {
  const { weights, setWeight, reset } = useLensWeights();
  const touched = lenses.filter((l) => lensWeight(weights, l.id) !== 1);

  return (
    <div>
      {touched.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span className="trust-muted" style={{ fontSize: '12px' }}>
            {touched.length} adjusted — scores are YOUR blend, not the default
          </span>
          <button onClick={reset} style={{ fontSize: '12px' }}>
            reset
          </button>
        </div>
      )}
      <div className="lens-weights-grid" style={{ gridTemplateColumns: '1fr' }}>
        {lenses.map((lens) => {
          const w = lensWeight(weights, lens.id);
          return (
            <label key={lens.id} title={lens.description}>
              <span className="lens-weights-name">
                {lens.label}
                <strong>{w.toFixed(1)}×</strong>
              </span>
              <input
                type="range"
                min={0}
                max={2}
                step={0.1}
                value={w}
                onChange={(e) => setWeight(lens.id, parseFloat(e.target.value))}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}
